import { Check, X, CircleHelp, Clock3, Crown } from "lucide-react";

export interface Attendee {
  email: string;
  name?: string;
  response: "accepted" | "declined" | "tentative" | "needsAction";
  organizer?: boolean;
  self?: boolean;
  optional?: boolean;
}

/** Icono + color por estado de respuesta. Mismo lenguaje visual que
 * Google: verde aceptó, rojo declinó, ámbar tal vez, gris pendiente. */
function ResponseIcon({ status }: { status: Attendee["response"] }) {
  if (status === "accepted") {
    return (
      <span
        className="size-4 rounded-full bg-success/15 text-success grid place-items-center"
        title="Aceptó"
      >
        <Check className="size-2.5" />
      </span>
    );
  }
  if (status === "declined") {
    return (
      <span
        className="size-4 rounded-full bg-error/15 text-error grid place-items-center"
        title="Declinó"
      >
        <X className="size-2.5" />
      </span>
    );
  }
  if (status === "tentative") {
    return (
      <span
        className="size-4 rounded-full bg-warning/15 text-warning grid place-items-center"
        title="Tal vez"
      >
        <CircleHelp className="size-2.5" />
      </span>
    );
  }
  return (
    <span
      className="size-4 rounded-full bg-base-300/50 text-base-content/55 grid place-items-center"
      title="Sin responder"
    >
      <Clock3 className="size-2.5" />
    </span>
  );
}

/**
 * Lista de invitados a un evento. Ordena: organizador primero, luego
 * los que aceptaron, tentativos, sin responder, declinaron al final.
 * Muestra nombre (o email si falta nombre) + chip con estado.
 *
 * Pensada para el detalle de evento del usuario — NUNCA se renderiza
 * sobre invitados de un evento de otra persona (el backend ya los
 * blanquea en ese caso).
 */
export function AttendeeList({
  attendees,
  compact = false,
}: {
  attendees: Attendee[];
  compact?: boolean;
}) {
  if (!attendees || attendees.length === 0) return null;
  const order = { accepted: 1, tentative: 2, needsAction: 3, declined: 4 } as const;
  const sorted = [...attendees].toSorted((a, b) => {
    if (a.organizer && !b.organizer) return -1;
    if (!a.organizer && b.organizer) return 1;
    return order[a.response] - order[b.response];
  });
  const counts = attendees.reduce(
    (acc, a) => {
      acc[a.response] = (acc[a.response] ?? 0) + 1;
      return acc;
    },
    {} as Record<Attendee["response"], number>,
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-base-content/55">
          Invitados · {attendees.length}
        </span>
        <span className="text-[10px] text-base-content/50 tabular-nums flex items-center gap-1.5">
          {counts.accepted ? <span className="text-success">✓ {counts.accepted}</span> : null}
          {counts.tentative ? <span className="text-warning">? {counts.tentative}</span> : null}
          {counts.needsAction ? (
            <span className="text-base-content/55">· {counts.needsAction}</span>
          ) : null}
          {counts.declined ? <span className="text-error">✗ {counts.declined}</span> : null}
        </span>
      </div>
      <ul className={compact ? "space-y-0.5" : "space-y-1"}>
        {sorted.map((a) => (
          <li key={a.email} className="flex items-center gap-2 text-sm" title={a.email}>
            <ResponseIcon status={a.response} />
            <span
              className={`truncate min-w-0 flex-1 ${
                a.response === "declined" ? "line-through text-base-content/45" : ""
              } ${a.self ? "font-medium" : ""}`}
            >
              {a.name ?? a.email}
              {a.self && <span className="ml-1 text-[10px] text-base-content/45">(tú)</span>}
            </span>
            {a.organizer && <Crown className="size-3 text-warning shrink-0" />}
            {a.optional && (
              <span className="text-[9px] uppercase tracking-wider text-base-content/45 shrink-0">
                opcional
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
